import { IntlShape } from 'react-intl'
import * as Yup from 'yup'

import { DEFAULT_MAX_QUANTITY } from '@/components/MenuItem/utils'
import { GuestMenuItem } from '@/gql/graphql'
import {
  ModifierGroupPartsFragment,
  UpgradeGroupPartsFragment,
  UpsellGroupPartsFragment,
} from '@/lib/gql.types'

import { getOptionGroupSchema } from './getOptionGroupSchema'

export const getValidationSchema = (
  {
    menuItem,
    upgradeGroups,
    upsellGroups,
    modifierGroups,
    validateStockEnabled = false,
  }: {
    menuItem: Pick<
      GuestMenuItem,
      'minQuantity' | 'inventoryCount' | 'inventoryEnabled' | 'maxQuantity'
    >
    upgradeGroups: UpgradeGroupPartsFragment[]
    upsellGroups: UpsellGroupPartsFragment[]
    modifierGroups: ModifierGroupPartsFragment[]
    validateStockEnabled?: boolean
  },
  intl: IntlShape,
) => {
  const newSchema = Yup.object().shape({
    item: Yup.object({
      id: Yup.string().required(),
      quantity: Yup.number()
        .min(menuItem.minQuantity || 1, () =>
          intl.formatMessage(
            {
              defaultMessage:
                'This menu item has a minimum order requirement of {itemQuantity}.',
              id: 'f3EIiT',
            },
            { itemQuantity: menuItem.minQuantity || 1 },
          ),
        )
        .max(
          Math.min(
            menuItem.maxQuantity || DEFAULT_MAX_QUANTITY,
            DEFAULT_MAX_QUANTITY,
          ),
          () =>
            intl.formatMessage(
              {
                defaultMessage:
                  'This menu item has a maximum order requirement of {maxQuantity}.',
                id: 'rnf3FZ',
              },
              { maxQuantity: menuItem.maxQuantity || DEFAULT_MAX_QUANTITY },
            ),
        )
        .required(),
      notes: Yup.string().max(
        20,
        intl.formatMessage({
          defaultMessage: 'Must be 20 characters or less.',
          id: 'l3p42b',
        }),
      ),
    }),
    upgrades: getOptionGroupSchema(upgradeGroups, intl, 'upgrades'),
    modifiers: getOptionGroupSchema(modifierGroups, intl, 'modifiers'),
    upsells: getOptionGroupSchema(upsellGroups, intl, 'upsells'),
  })

  if (validateStockEnabled) {
    return newSchema
  }

  const oldSchema = Yup.object().shape({
    item: Yup.object({
      id: Yup.string().required(),
      quantity: Yup.number()
        .min(menuItem.minQuantity || 1, () =>
          intl.formatMessage(
            {
              defaultMessage:
                'This menu item has a minimum order requirement of {itemQuantity}.',
              id: 'f3EIiT',
            },
            { itemQuantity: menuItem.minQuantity || 1 },
          ),
        )
        .max(
          Math.min(
            menuItem.inventoryEnabled && menuItem.inventoryCount != null
              ? Math.max(0, menuItem.inventoryCount)
              : DEFAULT_MAX_QUANTITY,
            menuItem.maxQuantity || DEFAULT_MAX_QUANTITY,
          ),
          () =>
            // if the lower limit is inventoryCount
            menuItem.inventoryEnabled &&
            menuItem.inventoryCount != null &&
            menuItem.inventoryCount <=
              (menuItem.maxQuantity || DEFAULT_MAX_QUANTITY)
              ? intl.formatMessage(
                  {
                    defaultMessage:
                      'There is only {inventoryCount} {inventoryCount, plural, one {item} other {items}} left of this product',
                    id: 'iGejse',
                  },
                  { inventoryCount: menuItem.inventoryCount || 1 },
                )
              : intl.formatMessage(
                  {
                    defaultMessage:
                      'This menu item has a maximum order requirement of {maxQuantity}.',
                    id: 'rnf3FZ',
                  },
                  { maxQuantity: menuItem.maxQuantity || DEFAULT_MAX_QUANTITY },
                ),
        )
        .required(),
      notes: Yup.string().max(
        20,
        intl.formatMessage({
          defaultMessage: 'Must be 20 characters or less.',
          id: 'l3p42b',
        }),
      ),
    }),
    upgrades: getOptionGroupSchema(upgradeGroups, intl, 'upgrades'),
    modifiers: getOptionGroupSchema(modifierGroups, intl, 'modifiers'),
    upsells: getOptionGroupSchema(upsellGroups, intl, 'upsells'),
  })

  return oldSchema
}
